/**
 * Settings Service
 * Handles pricing and notification settings API calls
 */

import api from './api';

const settingsService = {
  // =========================================================================
  // Pricing Settings
  // =========================================================================

  /**
   * Get pricing settings, optionally merged with route override
   * @param {string} routeId - Optional route ID for merged settings
   * @returns {Promise<Object>} Pricing settings
   */
  getPricingSettings: async (routeId = null) => {
    const params = routeId ? `?route_id=${routeId}` : '';
    const response = await api.get(`/v1/settings/pricing${params}`);
    return response.data;
  },

  /**
   * Update tenant-level pricing settings
   * @param {Object} settings - Settings to update
   * @returns {Promise<Object>} Updated settings
   */
  updatePricingSettings: async (settings) => {
    const response = await api.put('/v1/settings/pricing', settings);
    return response.data;
  },

  // =========================================================================
  // Route Overrides
  // =========================================================================

  /**
   * Get all route-specific overrides
   * @returns {Promise<Object>} List of route overrides
   */
  getRouteOverrides: async () => {
    const response = await api.get('/v1/settings/pricing/routes');
    return response.data;
  },

  /**
   * Update route-specific override
   * @param {string} routeId - Route identifier
   * @param {Object} settings - Settings to override
   * @returns {Promise<Object>} Result
   */
  updateRouteOverride: async (routeId, settings) => {
    const response = await api.put(`/v1/settings/pricing/routes/${routeId}`, settings);
    return response.data;
  },

  /**
   * Delete route-specific override
   * @param {string} routeId - Route identifier
   * @returns {Promise<Object>} Result
   */
  deleteRouteOverride: async (routeId) => {
    const response = await api.delete(`/v1/settings/pricing/routes/${routeId}`);
    return response.data;
  },

  // =========================================================================
  // Pricing System Configuration (Presets & Estimation)
  // =========================================================================

  /**
   * Get available presets and validation limits
   * @returns {Promise<Object>} Presets and limits
   */
  getPresets: async () => {
    const response = await api.get('/v1/settings/pricing/presets');
    return response.data;
  },

  /**
   * Estimate LLM calls for a configuration
   * @param {Object} systemConfig - System configuration to estimate
   * @returns {Promise<Object>} Estimation result
   */
  estimateCalls: async (systemConfig) => {
    const response = await api.post('/v1/settings/pricing/estimate', { system_config: systemConfig });
    return response.data;
  },

  // =========================================================================
  // Notification Settings
  // =========================================================================

  /**
   * Get notification preferences
   * @returns {Promise<Object>} Notification settings
   */
  getNotificationSettings: async () => {
    const response = await api.get('/v1/settings/notifications');
    return response.data;
  },

  /**
   * Update notification preferences
   * @param {Object} settings - Notification settings
   * @returns {Promise<Object>} Updated settings
   */
  updateNotificationSettings: async (settings) => {
    const response = await api.put('/v1/settings/notifications', settings);
    return response.data;
  },

  // =========================================================================
  // Legacy methods (keep for backwards compatibility)
  // =========================================================================

  /**
   * @deprecated Use getPricingSettings instead
   */
  getSettings: async () => {
    return settingsService.getPricingSettings();
  },

  /**
   * @deprecated Use updatePricingSettings instead
   */
  updateSettings: async (settings) => {
    return settingsService.updatePricingSettings(settings);
  },

  /**
   * @deprecated Use getPricingSettings instead
   */
  getPricingConstraints: async () => {
    return settingsService.getPricingSettings();
  },

  /**
   * @deprecated Use updatePricingSettings instead
   */
  updatePricingConstraints: async (constraints) => {
    return settingsService.updatePricingSettings(constraints);
  },

  /**
   * @deprecated Use getNotificationSettings instead
   */
  getNotifications: async () => {
    return settingsService.getNotificationSettings();
  },

  /**
   * @deprecated Use updateNotificationSettings instead
   */
  updateNotifications: async (notifications) => {
    return settingsService.updateNotificationSettings(notifications);
  },
};

export default settingsService;
